/**
 * @file Hooks for the Guide Generator UI.
 *
 * Provides data fetching hooks for guide variants, audience roles, and guide generation.
 */

import { useMutation, useQuery } from "@tanstack/react-query";

import type { GuideGeneratorInput } from "@/lib/features/guide-generator/types";

import { useSdk } from "@/app/guide-generator/_helpers/sdk";

/**
 * Fetch all guide variants for selection in the generator UI.
 *
 * @returns Query result with list of guide variants sorted by title.
 */
export const useGuideVariantListQuery = () => {
  const { auth, sdk } = useSdk();
  return useQuery({
    queryFn: async () => {
      if (!auth.data) {
        return [];
      }

      const result = await sdk.payload.find({
        collection: `guide-variant`,
        depth: 1,
        limit: 5000,
        locale: `en-US`,
        populate: {
          document: {
            title: true,
          },
        },
      });

      return result.docs
        .map(({ document, id, primaryUri }) => {
          if (typeof document === `string`) {
            throw new TypeError(`Document is a string`);
          }
          return {
            id,
            primaryUri: primaryUri ?? null,
            title: document.title,
          };
        })
        .toSorted((a, b) => a.title.localeCompare(b.title));
    },
    queryKey: [`guide-variant`, auth.data?.email],
  });
};
export type GuideVariantListQuery = ReturnType<typeof useGuideVariantListQuery>;

/**
 * Fetch all audience roles for the target audience selector.
 *
 * @returns Query result with list of available roles.
 */
export const useAudienceRoleListQuery = () => {
  const { auth, sdk } = useSdk();
  return useQuery({
    queryFn: async () => {
      if (!auth.data) {
        return [];
      }
      const response = await sdk.payload.find({
        collection: `role`,
        depth: 0,
        locale: `en-US`,
      });
      return response.docs;
    },
    queryKey: [`role`, auth.data?.email],
  });
};
export type AudienceRoleListQuery = ReturnType<typeof useAudienceRoleListQuery>;

/**
 * Mutation hook for generating a new guide via the API.
 *
 * @returns Mutation object for triggering guide generation.
 */
export const useGuideGenerateMutation = () => {
  const { sdk } = useSdk();
  return useMutation({
    mutationFn: async (input: Omit<GuideGeneratorInput, `aiApiKey`>) => {
      const response = await sdk.api.POST(`/api/~/guide-generator/generate`, {
        body: input,
      });

      if (!response.data) {
        throw new Error(`Failed to generate guide: ${String(response.error)}`);
      }

      return response.data;
    },
  });
};
export type GuideGenerateMutation = ReturnType<typeof useGuideGenerateMutation>;
